/**
 * Read-only label + value cell for JE/transaction preview summary grids
 * (DepositToSafePreview, ReverseJournalEntryPreview, JournalEntryPreview, etc).
 * Pass `value` for plain read-only display. Pass `children` instead when the
 * value slot needs to render a control or a richer node (loading state,
 * secondary helper text, multiple lines) — `value` is ignored whenever
 * `children` is provided. Use `<PreviewInput />` inside `children` for an
 * editable text/number/date field styled to match.
 */
export default function PreviewField({ label, value, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide text-palette-gray-secondary">{label}</span>
      {children !== undefined ? (
        children
      ) : (
        <span className="text-sm font-medium text-palette-gray-text break-words">
          {String(value ?? "—")}
        </span>
      )}
    </div>
  );
}

/**
 * Shared styling for an editable text/number/date field that matches
 * PreviewField's value text. Exported for the rare case a raw <input> is
 * needed outside a <PreviewField> (e.g. a table cell with its own layout) —
 * prefer <PreviewInput /> inside <PreviewField> when possible.
 */
export const PREVIEW_INPUT_CLASSES =
  "text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-blue-label";

/**
 * Text/number/date input styled to match PreviewField's value text. Use
 * inside <PreviewField> when a summary cell is editable, e.g.
 * <PreviewField label="Memo"><PreviewInput value={memo} onChange={...} /></PreviewField>
 */
export function PreviewInput({ className = "", ...props }) {
  return <input {...props} className={`${PREVIEW_INPUT_CLASSES} ${className}`} />;
}
